<template>
    <div>
        <ui-section title="Identificação principal" description="Dados que permitem localizar e classificar o processo.">
            <v-row dense>
                <v-col v-if="showClient" cols="12" md="6">
                    <v-autocomplete v-model="form.client_id" :items="catalogs.clients" item-value="id" item-text="name" label="Caso *" dense outlined
                        hide-details="auto" :rules="[$rules.required]" :disabled="disabled" no-data-text="Nenhum caso encontrado"></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                    <v-select v-model="form.nature" :items="natures" item-value="id" item-text="name" label="Natureza *" dense outlined hide-details="auto"
                        :rules="[$rules.required]" :disabled="disabled" @change="onNatureChange"></v-select>
                </v-col>
                <v-col cols="12" md="3">
                    <v-select v-model="form.process_type" :items="filteredTypes" item-value="id" item-text="name" label="Tipo de processo *" dense outlined
                        hide-details="auto" :rules="[$rules.required]" :disabled="disabled"></v-select>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field v-model="form.process_number" label="Número do processo" dense outlined hide-details="auto" :disabled="disabled"
                        hint="Opcional em processos administrativos." persistent-hint></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field v-model="form.subject" label="Assunto / objeto *" dense outlined hide-details="auto" :rules="[$rules.required]" :disabled="disabled"></v-text-field>
                </v-col>
            </v-row>
        </ui-section>

        <ui-section title="Órgão e localização" description="Onde o processo tramita e a unidade julgadora.">
            <v-row dense>
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.tribunal_or_agency" label="Tribunal ou órgão" dense outlined hide-details="auto" :disabled="disabled" placeholder="Ex.: TRT da 4ª Região"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.justice_branch" label="Ramo da Justiça" dense outlined hide-details="auto" :disabled="disabled" placeholder="Ex.: Justiça do Trabalho"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.court_division" label="Vara, turma ou unidade" dense outlined hide-details="auto" :disabled="disabled"></v-text-field>
                </v-col>
                <v-col cols="12" md="8">
                    <v-text-field v-model="form.district" label="Comarca / localidade" dense outlined hide-details="auto" :disabled="disabled"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                    <v-select v-model="form.state" :items="states" item-value="id" item-text="name" label="UF" dense outlined hide-details="auto" clearable :disabled="disabled"></v-select>
                </v-col>
            </v-row>
        </ui-section>

        <ui-section title="Acompanhamento interno" description="Responsável e orientações para a equipe.">
            <v-row dense>
                <v-col cols="12" md="4">
                    <v-select v-model="form.status" :items="statuses" item-value="id" item-text="name" :label="isNew ? 'Status inicial' : 'Status'" dense outlined hide-details="auto" :disabled="disabled"></v-select>
                </v-col>
                <v-col cols="12" md="8">
                    <v-autocomplete v-model="form.responsible_admin_id" :items="catalogs.admins" item-value="id" item-text="name" label="Responsável" dense outlined
                        hide-details="auto" clearable :disabled="disabled" no-data-text="Nenhum administrador encontrado"></v-autocomplete>
                </v-col>
                <v-col cols="12">
                    <v-textarea v-model="form.internal_notes" label="Observações internas" rows="3" auto-grow dense outlined hide-details="auto" :disabled="disabled"></v-textarea>
                </v-col>
            </v-row>
        </ui-section>

        <ui-section v-if="isNew" title="Origem do conhecimento" description="Como a Administração Judicial tomou conhecimento do processo.">
            <v-row dense>
                <v-col cols="12" md="4">
                    <v-select v-model="form.knowledge_source.source_type" :items="knowledgeTypes" item-value="id" item-text="name" label="Como chegou à AJ? *" dense outlined
                        hide-details="auto" :rules="[$rules.required]" :disabled="disabled"></v-select>
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.knowledge_source.known_at" type="datetime-local" label="Data e hora do conhecimento" dense outlined hide-details="auto" :disabled="disabled"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.knowledge_source.channel" label="Canal" dense outlined hide-details="auto" :disabled="disabled" placeholder="Ex.: e-mail, PJe, balcão"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.knowledge_source.sender" label="Remetente" dense outlined hide-details="auto" :disabled="disabled"></v-text-field>
                </v-col>
                <v-col cols="12" md="8">
                    <v-text-field v-model="form.knowledge_source.description" label="Descrição" dense outlined hide-details="auto" :disabled="disabled"></v-text-field>
                </v-col>
            </v-row>
        </ui-section>
    </div>
</template>

<script>
import { natures, processTypes, statuses, knowledgeTypes } from "@/common/data/other-proceeding.data";

// Campos do cadastro do processo: usados na inclusao (com a origem do
// conhecimento) e no dialogo "Editar cadastro". O objeto `value` e editado no lugar.
export default {
    name: "OtherProceedingForm",

    props: {
        value: { type: Object, required: true },
        catalogs: { type: Object, default: () => ({ clients: [], admins: [] }) },
        isNew: { type: Boolean, default: false },
        showClient: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
    },

    data: () => ({
        natures, processTypes, statuses, knowledgeTypes,
        states: ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"].map((uf) => ({ id: uf, name: uf })),
    }),

    computed: {
        form() {
            return this.value;
        },
        filteredTypes() {
            return this.processTypes.filter((type) => !type.nature || !this.form.nature || type.nature === this.form.nature);
        },
    },

    methods: {
        onNatureChange() {
            const type = this.processTypes.find((item) => item.id === this.form.process_type);
            if (type && type.nature && type.nature !== this.form.nature) this.form.process_type = null;
        },
    },
};
</script>
